import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, PieChart, Download, Mail } from "lucide-react";

const CLUB_EMOJI = { Analistas: "🧠", Diplomatas: "🤝", Sentinelas: "🛡️", Exploradores: "🚀" };
const CLUB_COLOR = { Analistas: "#A855F7", Diplomatas: "#22C55E", Sentinelas: "#3B82F6", Exploradores: "#EAB308" };
const ROLE_PT = { consultant: "Consultores", assistant: "Assistentes", coach: "Secretários", staff: "Staff", admin: "Admin" };

export default function Composition() {
  const [data, setData] = useState(null);
  const [openClub, setOpenClub] = useState(null);

  useEffect(() => {
    api.get("/composition").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const weakest = useMemo(() => {
    if (!data) return null;
    return [...data.clubs].sort((a, b) => a.total - b.total)[0];
  }, [data]);

  const exportFile = async (format) => {
    try {
      const r = await api.get(`/composition/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `composicao_kwlead.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!data) return <div className="kw-overline">A carregar…</div>;
  const pctTested = data.total_people ? Math.round(100 * data.with_mbti / data.total_people) : 0;

  return (
    <div className="space-y-8 kw-fade-up" data-testid="composition-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kw-overline mb-2">Quem somos</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            Composição <span className="text-[#E50000]">KW Lead</span>
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-3xl">
            Todas as pessoas da KW Lead — consultores, assistentes, coaches e staff — agrupadas por clube
            e por perfil MBTI, independentemente da função que ocupam.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportFile("xlsx")} className="px-3 py-2 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white hover:border-white inline-flex items-center gap-1.5 transition-colors" data-testid="composition-export-xlsx">
            <Download className="w-3.5 h-3.5" /> Excel
          </button>
          <button onClick={() => exportFile("csv")} className="px-3 py-2 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white hover:border-white inline-flex items-center gap-1.5 transition-colors" data-testid="composition-export-csv">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Pessoas na KW Lead" value={data.total_people} color="#fff" testid="stat-total" />
        <Stat label="Com perfil MBTI" value={data.with_mbti} suffix={`${pctTested}%`} color="#22C55E" testid="stat-tested" />
        <Stat label="Sem teste feito" value={data.without_mbti.total} color="#EAB308" testid="stat-untested" />
        <Stat label="Clube mais pequeno" value={weakest?.total ?? 0} suffix={weakest?.club} color="#E50000" testid="stat-weakest" />
      </div>

      {weakest && weakest.total < 12 && (
        <div className="kw-card p-4 border-l-2 border-[#EAB308] flex items-start gap-3" data-testid="composition-alert">
          <AlertTriangle className="w-5 h-5 text-[#EAB308] shrink-0 mt-0.5" />
          <div className="text-sm text-[#A3A3A3]">
            <b className="text-white">{CLUB_EMOJI[weakest.club]} {weakest.club} tem apenas {weakest.total} pessoas.</b>{" "}
            Não chega para fechar um plantel de 8 concorrentes. Vale a pena uma ação dirigida
            a este clube — ou tirar o teste MBTI a mais gente, já que
            há <b className="text-white">{data.without_mbti.total}</b> pessoas sem perfil atribuído.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.clubs.map((c) => (
          <div key={c.club} className="kw-card p-0" data-testid={`composition-${c.club}`}>
            <div className="p-4 border-b border-[#222]">
              <div className="flex items-center gap-2">
                <span className="text-xl">{CLUB_EMOJI[c.club]}</span>
                <span className="font-display font-black uppercase text-base flex-1" style={{ color: CLUB_COLOR[c.club] }}>
                  {c.club}
                </span>
                <Link to={`/composition/${encodeURIComponent(c.club)}`} title="Ver todos os membros"
                  className="font-display text-3xl font-black hover:underline underline-offset-4 transition-colors"
                  style={{ textDecorationColor: CLUB_COLOR[c.club] }} data-testid={`club-total-link-${c.club}`}>
                  {c.total}
                </Link>
                <span className="text-[10px] text-[#737373]">{c.pct_of_mapped}%</span>
              </div>
              <div className="mt-3 h-1.5 bg-[#1A1A1A] overflow-hidden">
                <div className="h-full transition-all duration-500"
                  style={{ width: `${c.pct_of_mapped}%`, background: CLUB_COLOR[c.club] }} />
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[10px] uppercase tracking-widest text-[#737373]">
                {Object.entries(c.by_role).filter(([, n]) => n > 0).map(([r, n]) => (
                  <span key={r}>{ROLE_PT[r]}: <b className="text-white">{n}</b></span>
                ))}
              </div>
              {c.missing_profiles.length > 0 && (
                <div className="mt-2 text-[10px] uppercase tracking-widest text-[#EAB308]">
                  Sem ninguém: {c.missing_profiles.join(" · ")}
                </div>
              )}
            </div>
            <div className="divide-y divide-[#161616]">
              {c.profiles.map((p) => (
                <div key={p.mbti} className={`px-4 py-2.5 ${p.total === 0 ? "opacity-40" : ""}`}
                  data-testid={`profile-${p.mbti}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border w-14 text-center shrink-0"
                      style={{ borderColor: CLUB_COLOR[c.club], color: CLUB_COLOR[c.club] }}>
                      {p.mbti}
                    </span>
                    <span className="text-sm flex-1 min-w-0 truncate">{p.label}</span>
                    <span className="font-display font-black text-lg">{p.total}</span>
                  </div>
                  {p.total > 0 && (
                    <div className="mt-1.5 h-1 bg-[#1A1A1A] overflow-hidden">
                      <div className="h-full" style={{
                        width: `${c.total ? (100 * p.total / c.total) : 0}%`,
                        background: CLUB_COLOR[c.club], opacity: 0.6,
                      }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="kw-card p-0" data-testid="composition-untested">
        <button onClick={() => setOpenClub(openClub === "untested" ? null : "untested")}
          className="w-full p-4 flex items-center gap-3 hover:bg-[#111] transition-colors"
          data-testid="toggle-untested">
          <PieChart className="w-4 h-4 text-[#EAB308] shrink-0" />
          <span className="flex-1 text-left">
            <span className="block kw-overline text-[10px]">Ainda sem perfil MBTI</span>
            <span className="block text-sm text-[#A3A3A3]">
              {Object.entries(data.without_mbti.by_role).filter(([, n]) => n > 0)
                .map(([r, n]) => `${ROLE_PT[r]}: ${n}`).join(" · ")}
            </span>
          </span>
          <span className="font-display text-3xl font-black text-[#EAB308]">{data.without_mbti.total}</span>
          <ChevronRight className={`w-4 h-4 text-[#555] transition-transform ${openClub === "untested" ? "rotate-90" : ""}`} />
        </button>
        {openClub === "untested" && (
          <div className="border-t border-[#222]">
            <MbtiCampaign total={data.without_mbti.total} />
            <div className="max-h-96 overflow-y-auto divide-y divide-[#161616]">
            {data.without_mbti.people.map((p, i) => (
              <div key={`${p.email || p.name}-${i}`} className="px-4 py-2 flex items-center gap-3 text-xs">
                <span className="flex-1 min-w-0">
                  <span className="block font-bold truncate">{p.name}</span>
                  <span className="block text-[10px] text-[#737373] truncate">{p.email}</span>
                </span>
                <span className="text-[9px] uppercase tracking-widest text-[#555] shrink-0">{ROLE_PT[p.role]}</span>
                {p.email && <SendOneButton email={p.email} name={p.name} />}
              </div>
            ))}
          </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MbtiCampaign({ total }) {
  const [deadline, setDeadline] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailHtml, setEmailHtml] = useState(null);
  const testLink = `${window.location.origin}/teste-mbti`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(testLink);
      toast.success("Link copiado — já podes colá-lo onde quiseres");
    } catch {
      toast.error("Não consegui copiar; seleciona o link e copia à mão");
    }
  };

  const openPreview = async () => {
    try {
      const r = await api.get("/mbti-test/campaign/preview", { params: { deadline: deadline || undefined } });
      setEmailHtml(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendToMe = async () => {
    try {
      const r = await api.get("/mbti-test/campaign/preview", { params: { deadline: deadline || undefined, to: "me" } });
      toast.success(`Email de teste enviado para ${r.data.sent_to}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const dry = async () => {
    setBusy(true);
    try {
      const r = await api.post("/mbti-test/campaign", { dry_run: true, deadline: deadline || null });
      setPreview(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/mbti-test/campaign", { dry_run: false, deadline: deadline || null });
      toast.success(`${r.data.sent} email(s) enviado(s) · prazo ${r.data.deadline}`);
      setConfirm(false); setPreview(null);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className="p-4 bg-[#0F0F0F] border-b border-[#222] space-y-3" data-testid="mbti-campaign">
      <div className="flex items-center gap-2">
        <Mail className="w-4 h-4 text-[#E50000]" />
        <span className="kw-overline text-[10px]">Campanha do teste MBTI</span>
      </div>
      <p className="text-xs text-[#A3A3A3] max-w-2xl leading-relaxed">
        Envia a estas <b className="text-white">{total}</b> pessoas o link do teste interno
        (<code className="text-[#E50000]">/teste-mbti</code>, 3 minutos) com um prazo.
        O resultado entra sozinho no perfil e no clube. <b className="text-[#EAB308]">Nada é enviado sem a tua confirmação.</b>
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <code className="text-[10px] text-[#A3A3A3] bg-[#161616] border border-[#262626] px-2 py-1.5 truncate max-w-full"
          data-testid="mbti-test-link">{testLink}</code>
        <button onClick={copyLink}
          className="px-3 py-1.5 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white hover:border-white transition-colors"
          data-testid="copy-mbti-link">Copiar link</button>
        <button onClick={openPreview}
          className="px-3 py-1.5 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white hover:border-white transition-colors"
          data-testid="preview-mbti-email">Ver o email</button>
        <button onClick={sendToMe}
          className="px-3 py-1.5 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white hover:border-white transition-colors"
          data-testid="send-mbti-to-me">Enviar-me um teste</button>
        <span className="text-[10px] text-[#555]">abre sem precisar de conta — serve para WhatsApp ou email teu</span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="kw-overline text-[10px] block mb-1">Prazo (vazio = 7 dias)</label>
          <input type="date" className="kw-input !py-2" value={deadline}
            onChange={(e) => setDeadline(e.target.value)} data-testid="campaign-deadline" />
        </div>
        <button onClick={dry} disabled={busy}
          className="px-3 py-2 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white hover:border-white transition-colors disabled:opacity-40"
          data-testid="campaign-preview">
          Pré-visualizar destinatários
        </button>
        {preview && !confirm && (
          <button onClick={() => setConfirm(true)}
            className="px-3 py-2 border border-[#EAB308] text-[10px] uppercase tracking-widest font-bold text-[#EAB308] hover:bg-[#EAB308]/10 transition-colors"
            data-testid="campaign-arm">
            Autorizar envio a {preview.would_send}
          </button>
        )}
        {confirm && (
          <button onClick={send} disabled={busy} className="kw-btn-primary !py-2 !px-5 text-[10px]" data-testid="campaign-send">
            {busy ? "A enviar…" : `Confirmar e enviar ${preview.would_send} emails`}
          </button>
        )}
      </div>
      {preview && (
        <div className="text-[10px] uppercase tracking-widest text-[#737373]" data-testid="campaign-preview-result">
          {preview.would_send} destinatários · exemplo: {(preview.sample || []).slice(0, 3).join(", ")}
        </div>
      )}
      {emailHtml && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setEmailHtml(null)} data-testid="mbti-email-modal">
          <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-[680px] max-h-[88vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 p-4 border-b border-[#1F1F1F]">
              <div className="min-w-0">
                <div className="kw-overline">Assunto</div>
                <div className="text-sm truncate">{emailHtml.subject}</div>
                <div className="text-[10px] text-[#555] mt-1">Prazo no email: {emailHtml.deadline}</div>
              </div>
              <button onClick={() => setEmailHtml(null)}
                className="text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-white"
                data-testid="close-mbti-email">Fechar</button>
            </div>
            <iframe title="Pré-visualização do email" srcDoc={emailHtml.html}
              className="flex-1 w-full bg-white" style={{ minHeight: "60vh" }} />
          </div>
        </div>
      )}
    </div>
  );
}

function SendOneButton({ email, name }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/mbti-test/campaign", { dry_run: false, emails: [email] });
      if (r.data.sent) {
        setSent(true);
        toast.success(`Convite enviado a ${(name || email).split(" ")[0]} · prazo ${r.data.deadline}`);
      } else {
        toast.error("O email não chegou a sair. Tenta outra vez.");
      }
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <button onClick={send} disabled={busy || sent} title={`Enviar o teste a ${name}`}
      className="px-2 py-1 border border-[#333] text-[9px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white hover:border-white transition-colors disabled:opacity-40 shrink-0"
      data-testid={`send-mbti-${email}`}>
      {sent ? "Enviado" : busy ? "A enviar…" : "Enviar"}
    </button>
  );
}

const Stat = ({ label, value, suffix, color, testid }) => (
  <div className="kw-card p-4" data-testid={testid}>
    <div className="font-display text-4xl font-black" style={{ color }}>{value}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mt-1">{label}</div>
    {suffix && <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color }}>{suffix}</div>}
  </div>
);
